/**
 * AdminDashboard - Panel de administración completo
 * Gestión de usuarios, planes, permisos y datos
 */
import React, { useState, useEffect } from 'react';
import { 
  Users, CreditCard, TrendingUp, Crown, Star, Zap, 
  ArrowLeft, RefreshCw, Search, Filter, ChevronDown,
  Calendar, DollarSign, UserPlus, Activity, Trash2,
  Shield, ShieldOff, Eye, RotateCcw, AlertTriangle,
  X, Check, Info, Edit, Plus, Key
} from 'lucide-react';
import { toast } from 'sonner';
import TradegateAuditPanel from './TradegateAuditPanel';
import SchedulersPanel from './SchedulersPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminDashboard = ({ onBack, user }) => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const [planFilter, setPlanFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modales
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showResetModal, setShowResetModal] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/admin/stats`, { headers });
      if (!statsRes.ok) {
        if (statsRes.status === 403) {
          throw new Error('No tienes permisos de administrador');
        }
        throw new Error('Error al cargar estadísticas');
      }
      const statsData = await statsRes.json();
      setStats(statsData);

      // Fetch users
      const usersRes = await fetch(`${API_URL}/api/admin/users?limit=200`, { headers });
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData.users);
      }

      // Fetch transactions
      const txRes = await fetch(`${API_URL}/api/admin/transactions?limit=50`, { headers });
      if (txRes.ok) {
        const txData = await txRes.json();
        setTransactions(txData.transactions);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateUserPlan = async (userId, newPlan) => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/plan?new_plan=${newPlan}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        toast.success(`Plan actualizado a ${newPlan}`);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al actualizar plan');
      }
    } catch (err) {
      toast.error('Error al actualizar plan');
    }
  };

  const toggleAdmin = async (userId, isAdmin) => {
    setLoadingAction(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/admin?is_admin=${isAdmin}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        toast.success(isAdmin ? 'Usuario promovido a admin' : 'Permisos de admin revocados');
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al cambiar permisos');
      }
    } catch (err) {
      toast.error('Error al cambiar permisos');
    } finally {
      setLoadingAction(false);
    }
  };

  const deleteUser = async (userId) => {
    setLoadingAction(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowDeleteConfirm(null);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al eliminar usuario');
      }
    } catch (err) {
      toast.error('Error al eliminar usuario');
    } finally {
      setLoadingAction(false);
    }
  };

  const resetUserData = async (userId, options) => {
    setLoadingAction(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const params = new URLSearchParams();
      if (options.markets) params.append('reset_markets', 'true');
      if (options.investments) params.append('reset_investments', 'true');
      if (options.alerts) params.append('reset_alerts', 'true');
      
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/reset-data?${params.toString()}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Datos reseteados: ${data.actions.join(', ')}`);
        setShowResetModal(null);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al resetear datos');
      }
    } catch (err) {
      toast.error('Error al resetear datos');
    } finally {
      setLoadingAction(false);
    }
  };

  const viewUserDetails = async (userId) => {
    setLoadingAction(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setUserDetails(data);
        setShowUserModal(true);
      } else {
        toast.error('Error al cargar detalles');
      }
    } catch (err) {
      toast.error('Error al cargar detalles');
    } finally {
      setLoadingAction(false);
    }
  };

  const deleteTransaction = async (transactionId) => {
    if (!transactionId) {
      toast.error('ID de transacción no válido');
      return;
    }
    
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta transacción?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/admin/transactions/${transactionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        toast.success('Transacción eliminada');
        fetchData(); // Recargar datos
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al eliminar transacción');
      }
    } catch (err) {
      toast.error('Error al eliminar transacción');
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesPlan = planFilter === 'all' || 
      u.plan === planFilter || 
      (planFilter === 'ultimate' && u.plan === 'premium') ||
      (planFilter === 'pro' && u.plan === 'basic');
    const matchesSearch = searchTerm === '' || 
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesPlan && matchesSearch;
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getPlanBadge = (plan) => {
    switch (plan) {
      case 'ultimate': 
      case 'premium': return 'bg-zinc-900/60 text-zinc-100 border-gold-500/40';
      case 'pro':
      case 'basic': return 'bg-zinc-900/60 text-zinc-100 border-gold-500/40';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  const getPlanIcon = (plan) => {
    switch (plan) {
      case 'ultimate':
      case 'premium': return <Crown className="w-3.5 h-3.5" />;
      case 'pro':
      case 'basic': return <Star className="w-3.5 h-3.5" />;
      default: return <Zap className="w-3.5 h-3.5" />;
    }
  };

  const getPlanName = (plan) => {
    switch (plan) {
      case 'ultimate':
      case 'premium': return 'Ultimate';
      case 'pro':
      case 'basic': return 'Pro';
      default: return 'Gratis';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-gold-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Cargando panel de administración...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-red-400 mb-2">Error de Acceso</h2>
          <p className="text-slate-400 mb-4">{error}</p>
          <button
            onClick={onBack}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Shield className="w-6 h-6 text-gold-400" />
              Panel de Administración
            </h1>
            <p className="text-slate-400 text-sm">Gestión completa de usuarios y plataforma</p>
          </div>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-gold-500/20 rounded-lg flex items-center justify-center">
              <Users className="w-4 h-4 text-gold-400" />
            </div>
            <span className="text-slate-400 text-sm">Total Usuarios</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats?.total_users || 0}</p>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-green-400" />
            </div>
            <span className="text-slate-400 text-sm">Ingresos</span>
          </div>
          <p className="text-2xl font-bold text-white">{(stats?.total_revenue || 0).toFixed(2)}€</p>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-gold-500/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-gold-400" />
            </div>
            <span className="text-slate-400 text-sm">Conversión</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats?.conversion_rate || 0}%</p>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-zinc-500/20 rounded-lg flex items-center justify-center">
              <Activity className="w-4 h-4 text-zinc-400" />
            </div>
            <span className="text-slate-400 text-sm">Nuevos (7d)</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats?.recent_signups_7d || 0}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: 'users', label: 'Usuarios', icon: Users },
          { id: 'transactions', label: 'Transacciones', icon: CreditCard },
          { id: 'overview', label: 'Resumen', icon: Activity },
          { id: 'tradegate', label: 'Tradegate Audit', icon: Activity },
          { id: 'schedulers', label: 'Schedulers', icon: RefreshCw }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-gold-500 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
          {/* Filters */}
          <div className="p-4 border-b border-slate-700 flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar por email o nombre..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-gold-500"
                />
              </div>
            </div>
            <select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              className="px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-gold-500"
            >
              <option value="all">Todos los planes</option>
              <option value="free">Gratis</option>
              <option value="pro">Pro (€19.99)</option>
              <option value="ultimate">Ultimate (€29.99)</option>
            </select>
            <div className="text-slate-400 text-sm flex items-center">
              {filteredUsers.length} usuarios
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900/50">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Usuario</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Plan</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-slate-400">Activos</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-slate-400">Inv.</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Registro</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-slate-400">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-700/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {u.is_admin && (
                          <Shield className="w-4 h-4 text-gold-400" title="Administrador" />
                        )}
                        <div>
                          <p className="font-medium text-white">{u.name}</p>
                          <p className="text-sm text-slate-400">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={u.plan}
                        onChange={(e) => updateUserPlan(u.id, e.target.value)}
                        className={`px-2 py-1 rounded-lg text-xs font-medium border ${getPlanBadge(u.plan)} bg-transparent cursor-pointer`}
                      >
                        <option value="free" className="bg-slate-900">Gratis</option>
                        <option value="pro" className="bg-slate-900">Pro (€19.99)</option>
                        <option value="ultimate" className="bg-slate-900">Ultimate (€29.99)</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-center text-white">{u.markets_count}</td>
                    <td className="px-4 py-3 text-center text-white">{u.investments_count}</td>
                    <td className="px-4 py-3 text-sm text-slate-400">{formatDate(u.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        {/* Ver detalles */}
                        <button
                          onClick={() => viewUserDetails(u.id)}
                          className="p-1.5 hover:bg-slate-600 rounded-lg transition-colors text-slate-400 hover:text-white"
                          title="Ver detalles"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        
                        {/* Toggle Admin */}
                        <button
                          onClick={() => toggleAdmin(u.id, !u.is_admin)}
                          className={`p-1.5 hover:bg-slate-600 rounded-lg transition-colors ${
                            u.is_admin ? 'text-gold-400 hover:text-gold-300' : 'text-slate-400 hover:text-white'
                          }`}
                          title={u.is_admin ? 'Quitar admin' : 'Hacer admin'}
                        >
                          {u.is_admin ? <ShieldOff className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                        </button>
                        
                        {/* Reset datos */}
                        <button
                          onClick={() => setShowResetModal(u)}
                          className="p-1.5 hover:bg-gold-500/20 rounded-lg transition-colors text-slate-400 hover:text-gold-400"
                          title="Resetear datos"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                        
                        {/* Eliminar */}
                        <button
                          onClick={() => setShowDeleteConfirm(u)}
                          className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors text-slate-400 hover:text-red-400"
                          title="Eliminar usuario"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="p-8 text-center text-slate-400">
              No se encontraron usuarios
            </div>
          )}
        </div>
      )}

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900/50">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Usuario</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Plan</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-slate-400">Monto</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-slate-400">Estado</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Fecha</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Session ID</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-slate-400">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {transactions.map((tx, idx) => (
                  <tr key={tx.id || idx} className="hover:bg-slate-700/30 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-white text-sm">{tx.user_email}</p>
                        <p className="text-slate-500 text-xs font-mono">{tx.user_id?.substring(0, 12)}...</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getPlanBadge(tx.plan_id)}`}>
                        {getPlanIcon(tx.plan_id)}
                        {tx.plan_id?.charAt(0).toUpperCase() + tx.plan_id?.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-white">
                      {tx.amount?.toFixed(2)}€
                      <span className="text-slate-500 text-xs ml-1">{tx.currency?.toUpperCase()}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        tx.payment_status === 'paid' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {tx.payment_status === 'paid' ? 'Pagado' : 'Pendiente'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-400">
                      <div>
                        <p>{formatDate(tx.created_at)}</p>
                        <p className="text-xs text-slate-500">{new Date(tx.created_at).toLocaleTimeString('es-ES')}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-slate-500 font-mono">
                        {tx.session_id?.includes('live') ? (
                          <span className="text-green-400">LIVE</span>
                        ) : (
                          <span className="text-gold-400">TEST</span>
                        )}
                        {' '}{tx.session_id?.substring(0, 20)}...
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => deleteTransaction(tx.id)}
                        className="p-1.5 hover:bg-red-500/20 rounded-lg text-slate-400 hover:text-red-400 transition-colors"
                        title="Eliminar transacción"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {transactions.length === 0 && (
            <div className="p-8 text-center text-slate-400">
              No hay transacciones registradas
            </div>
          )}
        </div>
      )}

      {/* Tradegate Audit Tab */}
      {activeTab === 'tradegate' && <TradegateAuditPanel />}
      {activeTab === 'schedulers' && <SchedulersPanel />}

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Plan Distribution */}
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-gold-400" />
              Distribución por Plan
            </h3>
            <div className="space-y-4">
              {[
                { plan: 'free', label: 'Gratis', count: stats?.users_by_plan?.free || 0, color: 'bg-slate-500' },
                { plan: 'pro', label: 'Pro', count: (stats?.users_by_plan?.pro || 0) + (stats?.users_by_plan?.basic || 0), color: 'bg-gold-500' },
                { plan: 'ultimate', label: 'Ultimate', count: (stats?.users_by_plan?.ultimate || 0) + (stats?.users_by_plan?.premium || 0), color: 'bg-gold-500' }
              ].map(item => {
                const percentage = stats?.total_users ? (item.count / stats.total_users * 100).toFixed(1) : 0;
                return (
                  <div key={item.plan}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-400">{item.label}</span>
                      <span className="text-white">{item.count} ({percentage}%)</span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${item.color} rounded-full transition-all`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-zinc-400" />
              Últimas Transacciones
            </h3>
            <div className="space-y-3">
              {transactions.slice(0, 5).map((tx, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-700/50 last:border-0">
                  <div>
                    <p className="text-white text-sm">{tx.user_email}</p>
                    <p className="text-slate-500 text-xs">{tx.plan_id}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-green-400 font-mono">{tx.amount.toFixed(2)}€</p>
                    <p className="text-slate-500 text-xs">{formatDate(tx.created_at)}</p>
                  </div>
                </div>
              ))}
              {transactions.length === 0 && (
                <p className="text-slate-500 text-center py-4">Sin transacciones</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Confirmar Eliminación */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Eliminar Usuario</h3>
                <p className="text-slate-400 text-sm">Esta acción no se puede deshacer</p>
              </div>
            </div>
            
            <p className="text-slate-300 mb-4">
              ¿Estás seguro de que quieres eliminar a <strong className="text-white">{showDeleteConfirm.email}</strong>?
              Se eliminarán todos sus datos, inversiones y alertas.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => deleteUser(showDeleteConfirm.id)}
                disabled={loadingAction}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loadingAction ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Resetear Datos */}
      {showResetModal && (
        <ResetDataModal 
          user={showResetModal}
          onClose={() => setShowResetModal(null)}
          onReset={resetUserData}
          loading={loadingAction}
        />
      )}

      {/* Modal: Detalles de Usuario */}
      {showUserModal && userDetails && (
        <UserDetailsModal
          user={userDetails}
          onClose={() => { setShowUserModal(false); setUserDetails(null); }}
          onUpdate={() => { viewUserDetails(userDetails.id); fetchData(); }}
        />
      )}
    </div>
  );
};

// Componente: Modal Reset Datos
const ResetDataModal = ({ user, onClose, onReset, loading }) => {
  const [options, setOptions] = useState({
    markets: false,
    investments: false,
    alerts: false
  });

  const hasSelection = options.markets || options.investments || options.alerts;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-gold-400" />
            Resetear Datos
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <p className="text-slate-400 text-sm mb-4">
          Selecciona qué datos de <strong className="text-white">{user.email}</strong> quieres resetear:
        </p>
        
        <div className="space-y-3 mb-6">
          <label className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg cursor-pointer hover:bg-slate-700/50 transition-colors">
            <input 
              type="checkbox" 
              checked={options.markets}
              onChange={(e) => setOptions({ ...options, markets: e.target.checked })}
              className="w-4 h-4 rounded border-slate-600 text-gold-500 focus:ring-gold-500"
            />
            <div>
              <p className="text-white font-medium">Mercados/Activos</p>
              <p className="text-slate-500 text-xs">Elimina todos los activos guardados</p>
            </div>
          </label>
          
          <label className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg cursor-pointer hover:bg-slate-700/50 transition-colors">
            <input 
              type="checkbox" 
              checked={options.investments}
              onChange={(e) => setOptions({ ...options, investments: e.target.checked })}
              className="w-4 h-4 rounded border-slate-600 text-gold-500 focus:ring-gold-500"
            />
            <div>
              <p className="text-white font-medium">Inversiones</p>
              <p className="text-slate-500 text-xs">Borra precios de compra y cantidades</p>
            </div>
          </label>
          
          <label className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg cursor-pointer hover:bg-slate-700/50 transition-colors">
            <input 
              type="checkbox" 
              checked={options.alerts}
              onChange={(e) => setOptions({ ...options, alerts: e.target.checked })}
              className="w-4 h-4 rounded border-slate-600 text-gold-500 focus:ring-gold-500"
            />
            <div>
              <p className="text-white font-medium">Alertas de Precio</p>
              <p className="text-slate-500 text-xs">Elimina todas las alertas configuradas</p>
            </div>
          </label>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={() => onReset(user.id, options)}
            disabled={loading || !hasSelection}
            className="flex-1 px-4 py-2 bg-gold-500 hover:bg-gold-600 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Resetear
          </button>
        </div>
      </div>
    </div>
  );
};

// Componente: Modal Detalles Usuario (Editable)
const UserDetailsModal = ({ user, onClose, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: user.name || '',
    email: user.email || '',
    plan: user.plan || 'free',
    is_admin: user.is_admin || false
  });
  const [loading, setLoading] = useState(false);
  const [showAddInvestment, setShowAddInvestment] = useState(false);
  const [newInvestment, setNewInvestment] = useState({ ticker: '', numShares: '', purchasePrice: '' });
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const saveChanges = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/admin/users/${user.id}/update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editData)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Cambios guardados: ${data.changes.join(', ')}`);
        setIsEditing(false);
        onUpdate?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al guardar');
      }
    } catch (err) {
      toast.error('Error al guardar cambios');
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error('La contraseña debe tener al menos 4 caracteres');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/admin/users/${user.id}/set-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: newPassword })
      });
      
      if (res.ok) {
        toast.success('Contraseña cambiada correctamente');
        setShowPasswordChange(false);
        setNewPassword('');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al cambiar contraseña');
      }
    } catch (err) {
      toast.error('Error al cambiar contraseña');
    } finally {
      setLoading(false);
    }
  };

  const addInvestment = async () => {
    if (!newInvestment.ticker) {
      toast.error('Ticker requerido');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/admin/users/${user.id}/add-investment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ticker: newInvestment.ticker,
          numShares: parseFloat(newInvestment.numShares) || 0,
          purchasePrice: parseFloat(newInvestment.purchasePrice) || 0
        })
      });
      
      if (res.ok) {
        toast.success(`Inversión ${newInvestment.ticker} añadida`);
        setNewInvestment({ ticker: '', numShares: '', purchasePrice: '' });
        setShowAddInvestment(false);
        onUpdate?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al añadir inversión');
      }
    } catch (err) {
      toast.error('Error al añadir inversión');
    } finally {
      setLoading(false);
    }
  };

  const deleteInvestment = async (ticker) => {
    if (!confirm(`¿Eliminar inversión de ${ticker}?`)) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/admin/users/${user.id}/investment/${ticker}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success(`Inversión ${ticker} eliminada`);
        onUpdate?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al eliminar');
      }
    } catch (err) {
      toast.error('Error al eliminar inversión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 max-w-2xl w-full my-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Info className="w-5 h-5 text-gold-400" />
            {isEditing ? 'Editar Usuario' : 'Detalles del Usuario'}
          </h3>
          <div className="flex items-center gap-2">
            {!isEditing && (
              <>
                <button 
                  onClick={() => setShowPasswordChange(true)}
                  className="px-3 py-1.5 bg-gold-500/20 hover:bg-zinc-800/70 hover:border-gold-400 text-gold-400 rounded-lg text-sm flex items-center gap-1"
                >
                  <Key className="w-4 h-4" />
                  Contraseña
                </button>
                <button 
                  onClick={() => setIsEditing(true)}
                  className="px-3 py-1.5 bg-gold-500/20 hover:bg-zinc-800/70 hover:border-gold-400 text-gold-400 rounded-lg text-sm flex items-center gap-1"
                >
                  <Edit className="w-4 h-4" />
                  Editar
                </button>
              </>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Modal Cambiar Contraseña */}
        {showPasswordChange && (
          <div className="mb-6 bg-gold-500/10 border border-gold-500/30 rounded-lg p-4">
            <h4 className="text-gold-400 font-medium mb-3 flex items-center gap-2">
              <Key className="w-4 h-4" />
              Cambiar Contraseña
            </h4>
            <div className="flex gap-2">
              <input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nueva contraseña"
                className="flex-1 bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white"
              />
              <button
                onClick={() => { setShowPasswordChange(false); setNewPassword(''); }}
                className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={changePassword}
                disabled={loading}
                className="px-4 py-2 bg-gold-500 hover:bg-gold-600 text-white rounded-lg flex items-center gap-2"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Guardar
              </button>
            </div>
          </div>
        )}
        
        {/* Info básica */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="bg-slate-900/50 rounded-lg p-4">
            <p className="text-slate-500 text-xs mb-1">Email</p>
            {isEditing ? (
              <input
                type="email"
                value={editData.email}
                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white text-sm"
              />
            ) : (
              <p className="text-white font-medium">{user.email}</p>
            )}
          </div>
          <div className="bg-slate-900/50 rounded-lg p-4">
            <p className="text-slate-500 text-xs mb-1">Nombre</p>
            {isEditing ? (
              <input
                type="text"
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white text-sm"
              />
            ) : (
              <p className="text-white font-medium">{user.name}</p>
            )}
          </div>
          <div className="bg-slate-900/50 rounded-lg p-4">
            <p className="text-slate-500 text-xs mb-1">Plan</p>
            {isEditing ? (
              <select
                value={editData.plan}
                onChange={(e) => setEditData({ ...editData, plan: e.target.value })}
                className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white text-sm"
              >
                <option value="free">Gratis</option>
                <option value="pro">Pro (€19.99)</option>
                <option value="ultimate">Ultimate (€29.99)</option>
              </select>
            ) : (
              <p className="text-white font-medium capitalize">
                {user.plan === 'ultimate' || user.plan === 'premium' ? 'Ultimate' : 
                 user.plan === 'pro' || user.plan === 'basic' ? 'Pro' : 'Gratis'}
              </p>
            )}
          </div>
          <div className="bg-slate-900/50 rounded-lg p-4">
            <p className="text-slate-500 text-xs mb-1">Admin</p>
            {isEditing ? (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editData.is_admin}
                  onChange={(e) => setEditData({ ...editData, is_admin: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-600"
                />
                <span className="text-white text-sm">Es administrador</span>
              </label>
            ) : (
              <p className={user.is_admin ? "text-gold-400" : "text-slate-400"}>{user.is_admin ? 'Sí' : 'No'}</p>
            )}
          </div>
          <div className="bg-slate-900/50 rounded-lg p-4">
            <p className="text-slate-500 text-xs mb-1">Registro</p>
            <p className="text-white font-medium">{new Date(user.created_at).toLocaleString('es-ES')}</p>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-4">
            <p className="text-slate-500 text-xs mb-1">Stripe ID</p>
            <p className="text-white font-mono text-sm">{user.stripe_customer_id || 'N/A'}</p>
          </div>
        </div>

        {/* Botones de edición */}
        {isEditing && (
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
            >
              Cancelar
            </button>
            <button
              onClick={saveChanges}
              disabled={loading}
              className="px-4 py-2 bg-gold-500 hover:bg-gold-600 text-white rounded-lg flex items-center gap-2"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Guardar Cambios
            </button>
          </div>
        )}
        
        {/* Activos */}
        <div className="mb-6">
          <h4 className="text-white font-medium mb-2">Activos ({user.selected_markets?.length || 0})</h4>
          <div className="flex flex-wrap gap-2">
            {user.selected_markets?.map((m) => (
              <span key={m.ticker} className="px-2 py-1 bg-slate-700 rounded text-sm text-slate-300">
                {m.ticker}
              </span>
            )) || <span className="text-slate-500">Sin activos</span>}
          </div>
        </div>
        
        {/* Inversiones */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-white font-medium">Inversiones</h4>
            <button
              onClick={() => setShowAddInvestment(!showAddInvestment)}
              className="px-2 py-1 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded text-xs flex items-center gap-1"
            >
              <Plus className="w-3 h-3" />
              Añadir
            </button>
          </div>
          
          {/* Formulario añadir inversión */}
          {showAddInvestment && (
            <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-3 gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Ticker (ej: AAPL)"
                  value={newInvestment.ticker}
                  onChange={(e) => setNewInvestment({ ...newInvestment, ticker: e.target.value.toUpperCase() })}
                  className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-sm"
                />
                <input
                  type="number"
                  placeholder="Cantidad"
                  value={newInvestment.numShares}
                  onChange={(e) => setNewInvestment({ ...newInvestment, numShares: e.target.value })}
                  className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-sm"
                />
                <input
                  type="number"
                  placeholder="Precio compra"
                  value={newInvestment.purchasePrice}
                  onChange={(e) => setNewInvestment({ ...newInvestment, purchasePrice: e.target.value })}
                  className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-sm"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowAddInvestment(false)}
                  className="px-3 py-1.5 bg-slate-700 text-white rounded text-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={addInvestment}
                  disabled={loading}
                  className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded text-sm flex items-center gap-1"
                >
                  {loading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  Añadir
                </button>
              </div>
            </div>
          )}
          
          {Object.keys(user.investments || {}).length > 0 ? (
            <div className="bg-slate-900/50 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-500 text-xs">
                    <th className="text-left p-2">Ticker</th>
                    <th className="text-right p-2">Acciones</th>
                    <th className="text-right p-2">Precio Compra</th>
                    <th className="text-center p-2">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(user.investments).map(([ticker, data]) => (
                    <tr key={ticker} className="border-t border-slate-700/50">
                      <td className="p-2 text-white">{ticker}</td>
                      <td className="p-2 text-right text-slate-300">{data.numShares?.toFixed(2)}</td>
                      <td className="p-2 text-right text-slate-300">${data.purchasePrice?.toFixed(2)}</td>
                      <td className="p-2 text-center">
                        <button
                          onClick={() => deleteInvestment(ticker)}
                          className="p-1 hover:bg-red-500/20 rounded text-slate-400 hover:text-red-400"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-slate-500">Sin inversiones registradas</p>
          )}
        </div>
        
        {/* Transacciones */}
        <div>
          <h4 className="text-white font-medium mb-2">Historial de Pagos</h4>
          {user.transactions?.length > 0 ? (
            <div className="space-y-2">
              {user.transactions.map((tx) => (
                <div key={tx.session_id || tx.date || `tx-${tx.plan_id}-${tx.amount}`} className="flex justify-between items-center py-2 px-3 bg-slate-900/50 rounded-lg">
                  <div>
                    <span className="text-white">{tx.plan_id}</span>
                    <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                      tx.status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {tx.status}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-green-400 font-mono">{tx.amount?.toFixed(2)}€</span>
                    <span className="text-slate-500 text-xs ml-2">{tx.date ? new Date(tx.date).toLocaleDateString('es-ES') : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500">Sin historial de pagos</p>
          )}
        </div>
        
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
